import { memo, useEffect, useMemo, useState } from "react";
import { TvRadarMap } from "@/components/broadcast/TvMap";
import { FullScreenCamSlide } from "@/components/broadcast/slides/CameraSlides";
import { iconFor, GA_ICON_PATH, BOLT_PATH, RollingNumber } from "@/components/broadcast/shared";
import { TvBanner, useClock } from "@/components/broadcast/TvFrame";

const etFmt = (d, opts) => d.toLocaleString("en-US", { timeZone: "America/New_York", ...opts });

const TV_BLUE = "#1f63c9";
// Boards that should NOT appear in the rotating center stage: the cinematic
// full-bleed spots (handled as full-frame takeovers), the sponsor (shown in the
// header), the camera wall / map (we feature a single cam instead) and the
// duplicate temps board.
const CENTER_BLOCK = new Set([
  "intro", "bumper", "forecast-video", "sponsor", "cameras", "camera-map", "map",
  "promo-severe", "promo-source",
]);

// Rotation index derived from the wall clock so it stays continuous across
// component remounts (the dashboard unmounts for intro / bumper / cut-ins /
// promos, and plain useState would otherwise restart every rotation at 0).
function useTimeIndex(len, periodMs) {
  const [, force] = useState(0);
  useEffect(() => {
    if (len < 1) return undefined;
    const t = setInterval(() => force((n) => n + 1), periodMs);
    return () => clearInterval(t);
  }, [len, periodMs]);
  return len > 0 ? Math.floor(Date.now() / periodMs) % len : 0;
}

// Scale any full 1920x1080 broadcast board/cam into an arbitrary panel, keeping
// its pixel-perfect internal sizing. Cover = fill (crop margins), contain = letterbox.
function FitBoard({ children, w, h, fit = "cover" }) {
  const scale = fit === "cover" ? Math.max(w / 1920, h / 1080) : Math.min(w / 1920, h / 1080);
  return (
    <div style={{ width: w, height: h, position: "relative", overflow: "hidden", background: "#05070d" }}>
      <div
        style={{
          position: "absolute", left: "50%", top: "50%", width: 1920, height: 1080,
          transform: `translate(-50%,-50%) scale(${scale})`, transformOrigin: "center center",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function GaMark({ size = 56 }) {
  return (
    <svg viewBox="-4 -4 108 108" style={{ height: size, width: size }} className="shrink-0 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]" aria-hidden>
      <path d={GA_ICON_PATH} fill="#0a0f1c" stroke={TV_BLUE} strokeWidth="5" strokeLinejoin="round" />
      <path d={BOLT_PATH} fill="#ffd60a" stroke="#0a0f1c" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

// Georgia map with the active forecast region highlighted (bottom-left anchor).
const REGION_CLIP = {
  "North Georgia": { x: -6, y: -6, w: 116, h: 42 },
  "Central Georgia": { x: -6, y: 36, w: 116, h: 26 },
  "South Georgia": { x: -6, y: 62, w: 116, h: 48 },
  "Georgia Coast": { x: 60, y: 40, w: 50, h: 70 },
};
function RegionMap({ region, size = 100 }) {
  const c = REGION_CLIP[region] || REGION_CLIP["North Georgia"];
  const clipId = `reg-clip-${region.replace(/\s+/g, "-")}`;
  return (
    <svg viewBox="-4 -4 108 108" style={{ height: size, width: size }} className="shrink-0 drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]" data-testid="region-map" aria-hidden>
      <defs>
        <clipPath id={clipId}>
          <rect x={c.x} y={c.y} width={c.w} height={c.h} />
        </clipPath>
      </defs>
      <path d={GA_ICON_PATH} fill="#0e1b30" stroke="#2f4a70" strokeWidth="4" strokeLinejoin="round" />
      <g clipPath={`url(#${clipId})`}>
        <path d={GA_ICON_PATH} fill="#ffd60a" stroke="#ffd60a" strokeWidth="2" strokeLinejoin="round" />
      </g>
      <path d={GA_ICON_PATH} fill="none" stroke="#8fb4e8" strokeWidth="3" strokeLinejoin="round" />
    </svg>
  );
}

// ---- Zone 1: Branding (Georgia outline + lightning bolt lockup) + live ET clock ----
function BrandingCard() {
  const now = useClock(1000);
  return (
    <div className="gwn-panel absolute flex flex-col items-center justify-center gap-3 px-5" style={{ left: 16, top: 16, width: 480, height: 200, zIndex: 10 }} data-testid="branding-subscribe-card">
      <div className="flex items-center justify-center gap-4">
        <GaMark size={96} />
        <div className="min-w-0 leading-none" data-testid="gwn-logo-img">
          <div className="font-oswald text-[42px] font-black uppercase tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">Georgia</div>
          <div className="mt-1 font-oswald text-[18px] font-bold uppercase tracking-[0.22em]" style={{ color: "#5fa0ff" }}>Weather Network</div>
        </div>
      </div>
      <div className="flex w-full items-center justify-center gap-3 border-t border-white/10 pt-2" data-testid="branding-clock">
        <span className="font-oswald text-[34px] font-black uppercase leading-none tracking-wide text-white" data-testid="branding-clock-time">
          {etFmt(now, { hour: "numeric", minute: "2-digit" })}
          <span className="ml-1.5 text-[18px] font-bold" style={{ color: "#5fa0ff" }}>ET</span>
        </span>
        <span className="h-[26px] w-px bg-white/20" />
        <span className="font-oswald text-[20px] font-bold uppercase tracking-[0.1em] text-[#9db4d4]" data-testid="branding-clock-date">
          {etFmt(now, { weekday: "short", month: "short", day: "numeric" })}
        </span>
      </div>
    </div>
  );
}

// ---- Zone 2: Rotating side map panel — always-on radar (futurecast ↔ live) ----
const SideMapPanel = memo(function SideMapPanel() {
  const items = useMemo(() => ([
    { key: "futurecast", caption: "Futurecast Radar", node: <TvRadarMap mode="futurecast" /> },
    { key: "live", caption: "Live Radar", node: <TvRadarMap mode="live" /> },
  ]), []);
  const i = useTimeIndex(items.length, 18000);
  const cur = items[i % items.length];
  const W = 480, HEAD = 46, MAP_H = 614 - HEAD;
  return (
    <div className="gwn-panel absolute" style={{ left: 16, top: 228, width: W, height: 614, zIndex: 10 }} data-testid="side-map-panel">
      <div className="gwn-panel-head flex items-center gap-2 px-4" style={{ height: HEAD }} data-testid="side-map-caption-bar">
        <span className="h-[9px] w-[9px] rounded-full bg-[#ffd60a]" />
        <span className="font-oswald text-[22px] font-black uppercase tracking-[0.14em] text-white">{cur.caption}</span>
      </div>
      <FitBoard key={cur.key} w={W} h={MAP_H} fit="cover">{cur.node}</FitBoard>
    </div>
  );
});

// ---- Zone 3: Center stage (full-size rotating broadcast boards) ----
const CenterStage = memo(function CenterStage({ cameras, sponsors, loopSlides, onCenterChange, pinned }) {
  // Rotate through the enabled on-air loop boards (respecting the admin's
  // configured order) so the center stage is a true "board stage", not a fixed
  // handful of graphics. Falls back to cam + radars if the loop is warming up.
  const items = useMemo(() => {
    const list = (loopSlides || [])
      .filter((s) => s && s.node && !CENTER_BLOCK.has(s.id))
      .map((s) => ({ key: s.id, voiceId: s.id, caption: s.title, title: s.title, right: s.right, fullBleed: !!s.fullBleed, chromeless: !!s.chromeless, node: s.node }));
    if (list.length) return list;
    const fb = [];
    if ((cameras || []).length) fb.push({ key: "camera", voiceId: "cameras-featured", caption: "Live Camera", chromeless: true, node: <FullScreenCamSlide cameras={cameras} /> });
    fb.push({ key: "live", voiceId: "hrrr-reflectivity", caption: "Live Radar", title: "Live Radar", fullBleed: true, node: <TvRadarMap mode="live" /> });
    fb.push({ key: "future", voiceId: "futurecast", caption: "Futurecast Radar", title: "Futurecast Radar", fullBleed: true, node: <TvRadarMap mode="futurecast" /> });
    return fb;
  }, [loopSlides, cameras]);

  const rot = useTimeIndex(items.length, 22000);
  const pinIdx = pinned ? items.findIndex((it) => it.key === pinned) : -1;
  const i = pinIdx >= 0 ? pinIdx : rot;
  const cur = items[i % Math.max(items.length, 1)] || items[0];
  useEffect(() => { onCenterChange?.(cur?.voiceId); }, [cur?.voiceId, onCenterChange]);
  // "Up Next" board: the board the rotation will land on next (hidden when pinned
  // to a single board for preview, or when only one board is available).
  const nextItem = pinIdx < 0 && items.length > 1 ? items[(i + 1) % items.length] : null;
  const nextTitle = nextItem?.title || nextItem?.caption || null;

  // Sponsor rotation (continuous across remounts) shown as a subtle corner badge.
  const spIdx = useTimeIndex((sponsors || []).length, 15000);
  const sponsor = (sponsors || []).length ? sponsors[spIdx] : null;

  const W = 1392, H = 826;
  const showBanner = cur && cur.title && !cur.chromeless;
  return (
    <div className="gwn-panel absolute overflow-hidden" style={{ left: 512, top: 16, width: W, height: H, zIndex: 10 }} data-testid="center-stage-panel">
      <FitBoard key={cur?.key} w={W} h={H} fit="contain">
        {showBanner && <TvBanner title={cur.title} right={cur.right} compact={!!cur.fullBleed} />}
        <div className={`absolute inset-0 ${cur?.fullBleed ? "" : "tv-canvas-pad"}`}>{cur?.node}</div>
      </FitBoard>
      {!showBanner && cur?.caption && (
        <div className="pointer-events-none absolute bottom-3 left-3 z-20 rounded-[4px] bg-[#0a0f1c]/85 px-4 py-1.5 backdrop-blur-sm" data-testid="center-stage-caption">
          <span className="font-oswald text-[22px] font-black uppercase tracking-[0.12em] text-white">{cur.caption}</span>
        </div>
      )}
      {nextTitle && (
        <div
          data-testid="broadcast-next-up"
          className="pointer-events-none absolute right-3 top-3 z-20 flex max-w-[620px] items-stretch overflow-hidden rounded-[4px] bg-[#0a0f1c] shadow-[0_6px_20px_rgba(0,0,0,0.5)]"
        >
          <span className="flex shrink-0 items-center bg-[#ffd60a] px-3.5 font-oswald text-[17px] font-black uppercase tracking-[0.16em] text-[#0a0f1c]">
            Up Next
          </span>
          <span
            data-testid="broadcast-next-up-title"
            className="min-w-0 truncate px-4 py-2.5 font-oswald text-[26px] font-black uppercase tracking-wide text-white"
          >
            {nextTitle}
          </span>
        </div>
      )}
      {/* Corner mini-radar removed: the always-on left radar panel provides constant
          radar motion without ever overlapping a graphic. */}
      {sponsor && (
        <div className="pointer-events-none absolute bottom-3 right-3 z-20 flex items-center gap-2.5 rounded-[4px] bg-white/95 px-3.5 py-2 shadow-[0_6px_20px_rgba(0,0,0,0.4)]" data-testid="sponsor-slot-container">
          <div className="text-right leading-none">
            <div className="font-oswald text-[10px] font-bold uppercase tracking-[0.3em] text-[#5b6675]">Brought to you by</div>
            <div className="mt-0.5 font-oswald text-[17px] font-black uppercase tracking-wide text-[#0a0f1c]">{sponsor.name}</div>
          </div>
          {sponsor.logo_url && <img src={sponsor.logo_url} alt={sponsor.name} className="h-[36px] max-w-[120px] object-contain" />}
        </div>
      )}
    </div>
  );
});

// ---- Zone 4: Rotating regional five-day forecast strip ----
const REGIONS = [
  { name: "North Georgia", anchor: "atlanta", cities: ["atlanta", "athens", "rome"] },
  { name: "Central Georgia", anchor: "macon", cities: ["macon", "columbus", "augusta"] },
  { name: "South Georgia", anchor: "albany", cities: ["albany", "tifton", "valdosta"] },
  { name: "Georgia Coast", anchor: "savannah", cities: ["savannah", "brunswick", "waycross"] },
];

function buildDays(city) {
  const periods = city?.forecast_periods || [];
  const days = [];
  for (let k = 0; k < periods.length && days.length < 5; k++) {
    const p = periods[k];
    if (p.isDaytime !== true) continue;
    const night = periods.slice(k + 1).find((x) => x.isDaytime === false);
    const label = p.startTime
      ? new Date(p.startTime).toLocaleDateString("en-US", { weekday: "short", timeZone: "America/New_York" }).toUpperCase()
      : (p.name || "").slice(0, 3).toUpperCase();
    days.push({
      label,
      high: typeof p.temperature === "number" ? p.temperature : null,
      low: night && typeof night.temperature === "number" ? night.temperature : null,
      cond: p.shortForecast || "",
      precip: p.probabilityOfPrecipitation?.value || 0,
    });
  }
  return days;
}

const FiveDayStrip = memo(function FiveDayStrip({ cities }) {
  const regIdx = useTimeIndex(REGIONS.length, 30000);
  const region = REGIONS[regIdx % REGIONS.length];
  const byId = useMemo(() => Object.fromEntries((cities || []).map((c) => [c.id, c])), [cities]);
  const anchorCity = byId[region.anchor] || (cities || [])[0] || null;
  const days = buildDays(anchorCity);
  const keyCities = region.cities.map((id) => byId[id]?.name).filter(Boolean);
  const ANCHOR = 300;
  return (
    <div className="absolute flex gap-[10px]" style={{ left: 16, top: 854, width: 1888, height: 170, zIndex: 10 }} data-testid="five-day-forecast-strip">
      <div className="gwn-panel flex shrink-0 items-center gap-4 px-5" style={{ width: ANCHOR }} data-testid="five-day-anchor-cell">
        <RegionMap region={region.name} size={100} />
        <div className="min-w-0 leading-none">
          <div className="font-oswald text-[26px] font-black uppercase tracking-wide text-white" data-testid="five-day-region-name">{region.name}</div>
          <div className="mt-2 space-y-1">
            {keyCities.map((n) => (
              <div key={n} className="truncate font-oswald text-[19px] font-bold uppercase tracking-[0.1em] text-[#9db4d4]">{n}</div>
            ))}
          </div>
        </div>
      </div>
      {days.map((d, i) => (
        <div key={i} className="gwn-panel flex flex-1 flex-col overflow-hidden" data-testid={`five-day-card-${i}`}>
          <div className="gwn-panel-head flex items-center justify-center py-1">
            <span className="font-oswald text-[22px] font-black uppercase tracking-[0.12em] text-white">{d.label}</span>
          </div>
          <div className="flex flex-1 items-center justify-between px-4">
            <div className="flex flex-col items-center">
              {iconFor(d.cond, "h-[58px] w-[58px]")}
              {d.precip > 0 && <span className="mt-0.5 font-oswald text-[15px] font-bold text-[#5fa0ff]">{d.precip}%</span>}
            </div>
            <div className="text-right leading-none">
              <div className="font-oswald text-[46px] font-black text-white">{d.high != null ? <><RollingNumber value={d.high} delay={i * 60} />°</> : "—"}</div>
              <div className="mt-1 font-oswald text-[26px] font-bold text-[#9db4d4]">{d.low != null ? <><RollingNumber value={d.low} delay={i * 60 + 30} />°</> : "—"}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
});

export default function DashboardStage({ cities, cameras, sponsors, loopSlides, onCenterChange, pinnedCenter }) {
  return (
    <div className="absolute inset-0" data-testid="gwn-dashboard-stage">
      <div className="gwn-dash-bg" aria-hidden />
      <BrandingCard />
      <SideMapPanel />
      <CenterStage cameras={cameras} sponsors={sponsors} loopSlides={loopSlides} onCenterChange={onCenterChange} pinned={pinnedCenter} />
      <FiveDayStrip cities={cities} />
    </div>
  );
}
